import { Box, Stack } from '@mui/material';
import {
  Handle,
  Node as ReactFlowNode,
  NodeProps,
  Position,
} from '@xyflow/react';
import { useLayoutEffect, useRef, useState } from 'react';
import { CSSObject } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { ARROW_EDGE_COLORS_BY_VARIANT } from '@/components/diagrams/FlowChart/edges/ArrowEdge';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';

import {
  CHILDREN_COUPLE_HANDLE_ID,
  CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID,
  CHILDREN_SPOUSE_1_EX_HANDLE_ID,
  CHILDREN_SPOUSE_1_HANDLE_ID,
  CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID,
  CHILDREN_SPOUSE_2_EX_HANDLE_ID,
  CHILDREN_SPOUSE_2_HANDLE_ID,
  CONNECTOR_OFFSET,
  COUPLE_NODE_WIDTH,
  PARENTS_SPOUSE_1_EX_HANDLE_ID,
  PARENTS_SPOUSE_1_HANDLE_ID,
  PARENTS_SPOUSE_2_EX_HANDLE_ID,
  PARENTS_SPOUSE_2_HANDLE_ID,
  SPOUSE_CONNECTOR_OVERLAP,
  SPOUSE_CONNECTOR_WIDTH,
  T_CONNECTOR_EDGE_OVERLAP,
} from '../utils/elkLayout.constants';
import { ExSpouses } from './ExSpouses';
import { PersonData } from './PersonTile';
import { SpousePersonTile } from './SpousePersonTile';

const handleStyles: CSSObject = {
  position: 'absolute',
  pointerEvents: 'none',
  opacity: 0,
  width: 0,
  height: 0,
};

const useStyles = makeStyles<{
  width: number;
  spouse1: PersonData;
  selected?: boolean;
}>()((theme, { width, spouse1, selected }) => {
  return {
    root: {
      position: 'relative',
      width: `${width}px`,
      display: 'flex',
      justifyContent: 'center',
      ...(selected && {
        outlineColor: spouse1.isPrimary
          ? theme.palette.dataVisualizationPrimary.dark
          : theme.palette.dataVisualizationSecondary.dark,
        outlineOffset: '-1px',
        outlineStyle: 'solid',
        outlineWidth: '1px',
        borderRadius: '4px',
        backgroundColor: spouse1.isPrimary
          ? theme.palette.dataVisualizationPrimary.ultraLight
          : theme.palette.dataVisualizationSecondary.ultraLight,
      }),
    },
    topHandles: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      pointerEvents: 'none',
    },
    topHandleSpouse1Ex: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    topHandleSpouse1: {
      '&.react-flow__handle': {
        position: 'absolute',
        ...handleStyles,
      },
    },
    topHandleSpouse2: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    topHandleSpouse2Ex: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    bottomHandles: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      pointerEvents: 'none',
    },
    bottomHandleCenter: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    bottomHandleSpouse1: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    bottomHandleSpouse2: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    bottomHandleLeft: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    bottomHandleRight: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    bottomHandleLeftCoparent: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
    bottomHandleRightCoparent: {
      '&.react-flow__handle': {
        ...handleStyles,
      },
    },
  };
});

export interface MarriageGroupData extends Record<string, unknown> {
  id: string;
  spouse1: PersonData;
  spouse2?: PersonData; // Optional when representing a single person with ex-spouses
  spouse1ExSpouses: PersonData[];
  spouse2ExSpouses: PersonData[];
  hasCoupleChildren?: boolean;
  hasSpouse1ExCoparentChildren?: boolean; // Track if any children use spouse1 ex-spouse co-parent handle
  hasSpouse2ExCoparentChildren?: boolean; // Track if any children use spouse2 ex-spouse co-parent handle
  allChildren: string[];
  isCurrentGroup?: boolean;
}

export type MarriageGroupNodeProps = NodeProps<
  ReactFlowNode<MarriageGroupData>
>;

export function MarriageGroupNode({
  data,
  selected,
  ...tileProps
}: MarriageGroupNodeProps) {
  const {
    spouse1,
    spouse2,
    spouse1ExSpouses,
    spouse2ExSpouses,
    hasCoupleChildren,
    hasSpouse1ExCoparentChildren,
    hasSpouse2ExCoparentChildren,
    isCurrentGroup,
  } = data;

  const { isInteractive } = useIsInteractive();
  const width = tileProps.width ?? COUPLE_NODE_WIDTH;

  // Refs for measuring positions to align top handles with spouse tiles
  const rootRef = useRef<HTMLDivElement | null>(null);
  const spouse1Ref = useRef<HTMLDivElement | null>(null);
  const spouse2Ref = useRef<HTMLDivElement | null>(null);
  const leftExRef = useRef<HTMLDivElement | null>(null);
  const rightExRef = useRef<HTMLDivElement | null>(null);
  const coupleCenterHandleRef = useRef<HTMLDivElement | null>(null);
  const leftCoparentHandleRef = useRef<HTMLDivElement | null>(null);
  const rightCoparentHandleRef = useRef<HTMLDivElement | null>(null);
  const [spouse1CenterLeftPx, setSpouse1CenterLeftPx] = useState<number | null>(
    null
  );
  const [spouse2CenterLeftPx, setSpouse2CenterLeftPx] = useState<number | null>(
    null
  );
  const [leftExCenterLeftPx, setLeftExCenterLeftPx] = useState<number | null>(
    null
  );
  const [rightExCenterLeftPx, setRightExCenterLeftPx] = useState<number | null>(
    null
  );
  const [coupleCenterHandleTopPx, setCoupleCenterHandleTopPx] = useState<
    number | null
  >(null);
  const [leftCoparentHandleTopPx, setLeftCoparentHandleTopPx] = useState<
    number | null
  >(null);
  const [rightCoparentHandleTopPx, setRightCoparentHandleTopPx] = useState<
    number | null
  >(null);

  // Compute the midpoint between the two primary spouses for centering
  // If there's no spouse2, center on spouse1
  const coupleCenterLeftPx =
    spouse1CenterLeftPx !== null && spouse2CenterLeftPx !== null
      ? (spouse1CenterLeftPx + spouse2CenterLeftPx) / 2
      : spouse1CenterLeftPx !== null && !spouse2
        ? spouse1CenterLeftPx
        : null;
  // Position the T-connector to match the children-couple handle for seamless connection
  const tConnectorLeft =
    coupleCenterLeftPx !== null ? `${coupleCenterLeftPx}px` : '50%';

  useLayoutEffect(() => {
    const currentRoot = rootRef.current;

    // Helper function to calculate offset relative to root element
    const getOffsetRelativeToRoot = (
      element: HTMLElement,
      root: HTMLElement
    ) => {
      let top = 0;
      let left = 0;
      let current: HTMLElement | null = element;

      while (current && current !== root) {
        top += current.offsetTop;
        left += current.offsetLeft;
        current = current.offsetParent as HTMLElement | null;

        // Safety check: if we've gone outside the root, break
        if (current && !root.contains(current)) {
          break;
        }
      }

      return { top, left };
    };

    const computeCenters = () => {
      const root = currentRoot;
      const s1 = spouse1Ref.current;
      const s2 = spouse2Ref.current;
      const leftEx = leftExRef.current;
      const rightEx = rightExRef.current;
      const coupleHandle = coupleCenterHandleRef.current;
      const leftCoparentHandle = leftCoparentHandleRef.current;
      const rightCoparentHandle = rightCoparentHandleRef.current;
      if (!root) return;

      if (s1) {
        const { left } = getOffsetRelativeToRoot(s1, root);
        const s1Left = left + s1.offsetWidth / 2;
        setSpouse1CenterLeftPx(s1Left);
      } else {
        setSpouse1CenterLeftPx(null);
      }

      if (s2) {
        const { left } = getOffsetRelativeToRoot(s2, root);
        const s2Left = left + s2.offsetWidth / 2;
        setSpouse2CenterLeftPx(s2Left);
      } else {
        setSpouse2CenterLeftPx(null);
      }

      if (leftEx) {
        const { left } = getOffsetRelativeToRoot(leftEx, root);
        const leftExLeft = left + leftEx.offsetWidth / 2;
        setLeftExCenterLeftPx(leftExLeft);
      } else {
        setLeftExCenterLeftPx(null);
      }

      if (rightEx) {
        const { left } = getOffsetRelativeToRoot(rightEx, root);
        const rightExLeft = left + rightEx.offsetWidth / 2;
        setRightExCenterLeftPx(rightExLeft);
      } else {
        setRightExCenterLeftPx(null);
      }

      if (coupleHandle) {
        const { top } = getOffsetRelativeToRoot(coupleHandle, root);
        // Use full offsetHeight since we want to extend to the bottom edge of the handle
        const coupleHandleTop =
          top + coupleHandle.offsetHeight + T_CONNECTOR_EDGE_OVERLAP;
        setCoupleCenterHandleTopPx(coupleHandleTop);
      } else {
        setCoupleCenterHandleTopPx(null);
      }

      if (leftCoparentHandle) {
        const { top } = getOffsetRelativeToRoot(leftCoparentHandle, root);
        // Use full offsetHeight since we want to extend to the bottom edge of the handle
        const leftCoparentTop =
          top + leftCoparentHandle.offsetHeight + T_CONNECTOR_EDGE_OVERLAP;
        setLeftCoparentHandleTopPx(leftCoparentTop);
      } else {
        setLeftCoparentHandleTopPx(null);
      }

      if (rightCoparentHandle) {
        const { top } = getOffsetRelativeToRoot(rightCoparentHandle, root);
        // Use full offsetHeight since we want to extend to the bottom edge of the handle
        const rightCoparentTop =
          top + rightCoparentHandle.offsetHeight + T_CONNECTOR_EDGE_OVERLAP;
        setRightCoparentHandleTopPx(rightCoparentTop);
      } else {
        setRightCoparentHandleTopPx(null);
      }
    };

    computeCenters();
  }, [
    width,
    spouse1,
    spouse2,
    spouse1ExSpouses.length,
    spouse2ExSpouses.length,
    hasCoupleChildren,
    hasSpouse1ExCoparentChildren,
    hasSpouse2ExCoparentChildren,
    data.id,
  ]);

  const { classes } = useStyles({
    width,
    spouse1,
    selected: isInteractive ? selected : false,
  });

  // Generate test ID for identifying the marriage group by spouse IDs
  const testId = spouse2
    ? `marriage-group-${spouse1.personId}-${spouse2.personId}`
    : `marriage-group-${spouse1.personId}`;

  return (
    <div className={classes.root} ref={rootRef} data-testid={testId}>
      {/* T-shaped connector SVG positioned to align with children-couple handle */}
      {spouse2 && coupleCenterHandleTopPx !== null && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: tConnectorLeft,
            transform: `translateX(-50%)`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
          width={SPOUSE_CONNECTOR_WIDTH + SPOUSE_CONNECTOR_OVERLAP * 2}
          height={coupleCenterHandleTopPx}
          viewBox={`0 0 ${SPOUSE_CONNECTOR_WIDTH} ${coupleCenterHandleTopPx}`}
        >
          <line
            x1={`-${SPOUSE_CONNECTOR_OVERLAP}`}
            y1={CONNECTOR_OFFSET}
            x2={`${SPOUSE_CONNECTOR_WIDTH + SPOUSE_CONNECTOR_OVERLAP}`}
            y2={CONNECTOR_OFFSET}
            stroke={ARROW_EDGE_COLORS_BY_VARIANT.primary}
            strokeWidth="1"
            strokeDasharray={!isCurrentGroup ? '4 3' : undefined}
          />
          {hasCoupleChildren && coupleCenterHandleTopPx !== null && (
            <line
              x1={SPOUSE_CONNECTOR_WIDTH / 2}
              y1={CONNECTOR_OFFSET}
              x2={SPOUSE_CONNECTOR_WIDTH / 2}
              y2={coupleCenterHandleTopPx}
              stroke={ARROW_EDGE_COLORS_BY_VARIANT.primary}
              strokeWidth="1"
            />
          )}
        </svg>
      )}

      {/* Vertical line for single ex-spouse co-parent (spouse1) */}
      {hasSpouse1ExCoparentChildren &&
        spouse1ExSpouses.length === 1 &&
        leftExCenterLeftPx !== null &&
        spouse1CenterLeftPx !== null &&
        leftCoparentHandleTopPx !== null && (
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: `${(leftExCenterLeftPx + spouse1CenterLeftPx) / 2}px`,
              transform: `translateX(-50%)`,
              pointerEvents: 'none',
              zIndex: 1,
            }}
            width={2}
            height={leftCoparentHandleTopPx}
          >
            {/* Vertical line down to children handle (horizontal is handled by ExSpousesConnectorSvg) */}
            <line
              x1={1}
              y1={CONNECTOR_OFFSET}
              x2={1}
              y2={leftCoparentHandleTopPx}
              stroke={ARROW_EDGE_COLORS_BY_VARIANT.primary}
              strokeWidth="1"
            />
          </svg>
        )}

      {/* Vertical line for single ex-spouse co-parent (spouse2) */}
      {hasSpouse2ExCoparentChildren &&
        spouse2ExSpouses.length === 1 &&
        rightExCenterLeftPx !== null &&
        spouse2CenterLeftPx !== null &&
        rightCoparentHandleTopPx !== null && (
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: `${(rightExCenterLeftPx + spouse2CenterLeftPx) / 2}px`,
              transform: `translateX(-50%)`,
              pointerEvents: 'none',
              zIndex: 1,
            }}
            width={2}
            height={rightCoparentHandleTopPx}
          >
            {/* Vertical line down to children handle (horizontal is handled by ExSpousesConnectorSvg) */}
            <line
              x1={1}
              y1={CONNECTOR_OFFSET}
              x2={1}
              y2={rightCoparentHandleTopPx}
              stroke={ARROW_EDGE_COLORS_BY_VARIANT.primary}
              strokeWidth="1"
            />
          </svg>
        )}

      <div className={classes.topHandles}>
        {spouse1ExSpouses.length > 0 && (
          <Handle
            id={PARENTS_SPOUSE_1_EX_HANDLE_ID}
            type="target"
            position={Position.Top}
            className={classes.topHandleSpouse1Ex}
            style={{
              left:
                leftExCenterLeftPx !== null
                  ? `${leftExCenterLeftPx}px`
                  : undefined,
            }}
          />
        )}

        <Handle
          id={PARENTS_SPOUSE_1_HANDLE_ID}
          type="target"
          position={Position.Top}
          className={classes.topHandleSpouse1}
          style={{
            left:
              spouse1CenterLeftPx !== null
                ? `${spouse1CenterLeftPx}px`
                : undefined,
          }}
        />

        {spouse2 && (
          <Handle
            id={PARENTS_SPOUSE_2_HANDLE_ID}
            type="target"
            position={Position.Top}
            className={classes.topHandleSpouse2}
            style={{
              left:
                spouse2CenterLeftPx !== null
                  ? `${spouse2CenterLeftPx}px`
                  : undefined,
            }}
          />
        )}

        {spouse2ExSpouses.length > 0 && (
          <Handle
            id={PARENTS_SPOUSE_2_EX_HANDLE_ID}
            type="target"
            position={Position.Top}
            className={classes.topHandleSpouse2Ex}
            style={{
              left:
                rightExCenterLeftPx !== null
                  ? `${rightExCenterLeftPx / 1}px`
                  : undefined,
              transform: 'translate(-50%, 50%)',
            }}
          />
        )}
      </div>

      <div className={classes.bottomHandles}>
        {spouse1ExSpouses.length > 0 && (
          <>
            <Handle
              id={CHILDREN_SPOUSE_1_EX_HANDLE_ID}
              type="source"
              position={Position.Bottom}
              className={classes.bottomHandleLeft}
              style={{
                left:
                  leftExCenterLeftPx !== null
                    ? `${leftExCenterLeftPx}px`
                    : undefined,
                transform: 'translate(-50%, 50%)',
              }}
            />
            {/* Single ex-spouse co-parent handle (for T-connector) */}
            {spouse1ExSpouses.length === 1 && (
              <Handle
                id={CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID}
                type="source"
                position={Position.Bottom}
                className={classes.bottomHandleLeftCoparent}
                style={{
                  left:
                    leftExCenterLeftPx !== null && spouse1CenterLeftPx !== null
                      ? `${(leftExCenterLeftPx + spouse1CenterLeftPx) / 2}px`
                      : undefined,
                  transform: 'translate(-50%, 50%)',
                }}
                ref={leftCoparentHandleRef}
              />
            )}
          </>
        )}

        {/* Individual spouse1 children handle */}
        <Handle
          id={CHILDREN_SPOUSE_1_HANDLE_ID}
          type="source"
          position={Position.Bottom}
          className={classes.bottomHandleSpouse1}
          style={{
            left:
              spouse1CenterLeftPx !== null
                ? `${spouse1CenterLeftPx}px`
                : undefined,
            transform: 'translate(-50%, 50%)',
          }}
        />

        {/* Couple children handle (only if there's a spouse2) */}
        {spouse2 && (
          <Handle
            id={CHILDREN_COUPLE_HANDLE_ID}
            type="source"
            position={Position.Bottom}
            className={classes.bottomHandleCenter}
            style={{
              left: `${coupleCenterLeftPx}px`,
            }}
            ref={coupleCenterHandleRef}
          />
        )}

        {/* Individual spouse2 children handle */}
        {spouse2 && (
          <Handle
            id={CHILDREN_SPOUSE_2_HANDLE_ID}
            type="source"
            position={Position.Bottom}
            className={classes.bottomHandleSpouse2}
            style={{
              left:
                spouse2CenterLeftPx !== null
                  ? `${spouse2CenterLeftPx}px`
                  : undefined,
              transform: 'translate(-50%, 50%)',
            }}
          />
        )}

        {spouse2ExSpouses.length > 0 && (
          <>
            {/* Single ex-spouse co-parent handle (for T-connector) */}
            {spouse2ExSpouses.length === 1 && (
              <Handle
                id={CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID}
                type="source"
                position={Position.Bottom}
                className={classes.bottomHandleRightCoparent}
                style={{
                  left:
                    rightExCenterLeftPx !== null && spouse2CenterLeftPx !== null
                      ? `${(rightExCenterLeftPx + spouse2CenterLeftPx) / 2}px`
                      : undefined,
                  transform: 'translate(-50%, 50%)',
                }}
                ref={rightCoparentHandleRef}
              />
            )}
            <Handle
              id={CHILDREN_SPOUSE_2_EX_HANDLE_ID}
              type="source"
              position={Position.Bottom}
              className={classes.bottomHandleRight}
              style={{
                left:
                  rightExCenterLeftPx !== null
                    ? `${rightExCenterLeftPx}px`
                    : undefined,
                right: 'auto',
                transform: 'translate(-50%, 50%)',
              }}
            />
          </>
        )}
      </div>
      <Stack direction="row">
        {spouse1ExSpouses.length > 0 && (
          <ExSpouses
            id={tileProps.id}
            exSpouses={spouse1ExSpouses}
            position="left"
            containerRef={leftExRef}
            plural={spouse1ExSpouses.length > 1}
          />
        )}
        <div
          style={{
            position: 'relative',
            display: 'flex',
          }}
        >
          <div ref={spouse1Ref} style={{ position: 'relative' }}>
            <SpousePersonTile id={tileProps.id} person={spouse1} />
          </div>
          {spouse2 && (
            <>
              <Box display="flex" width={SPOUSE_CONNECTOR_WIDTH} />
              <div ref={spouse2Ref} style={{ position: 'relative' }}>
                <SpousePersonTile id={tileProps.id} person={spouse2} />
              </div>
            </>
          )}
        </div>
        {spouse2ExSpouses.length > 0 && (
          <ExSpouses
            id={tileProps.id}
            exSpouses={spouse2ExSpouses}
            position="right"
            containerRef={rightExRef}
            plural={spouse2ExSpouses.length > 1}
          />
        )}
      </Stack>
    </div>
  );
}
